import { useRef, useState } from 'react';
import { Download, DatabaseBackup, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import dbUrl, { PRODUCTION_DB_URL, isStagingDb } from '../../utils/credentials';
import {
  CURRENT_DB_LABEL,
  copyProductionToStaging,
  downloadBackup,
  fetchDatabase,
  parseBackupFile,
  replaceDatabase,
  summarizeDatabase,
} from '../../lib/databaseBackup';
import { toast } from '../../lib/toast';

const CONFIRM_WORD = 'SOVRASCRIVI';

function ToolRow({ label, description, children }) {
  return (
    <div className="flex flex-col gap-3 border-b border-[var(--ba-surface-border)] py-4 last:border-b-0 last:pb-0 first:pt-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[var(--ba-body-text)]">{label}</p>
        {description && <p className="mt-0.5 text-sm ba-muted">{description}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function SummaryList({ items }) {
  return (
    <ul className="max-h-40 overflow-y-auto rounded-lg border border-[var(--ba-surface-border)] px-3 py-2 text-sm">
      {items.map(({ node, count }) => (
        <li key={node} className="flex justify-between gap-4 py-0.5">
          <span className="font-mono">{node}</span>
          <span className="tabular-nums ba-muted">{count} elementi</span>
        </li>
      ))}
    </ul>
  );
}

function DatabaseToolsCard() {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // { kind: 'restore' | 'copy', data, summary, fileName }
  const [pending, setPending] = useState(null);
  const [backupFirst, setBackupFirst] = useState(true);
  const [typed, setTyped] = useState('');

  const closeDialog = () => {
    if (busy) return;
    setPending(null);
    setTyped('');
    setBackupFirst(true);
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      downloadBackup(await fetchDatabase(dbUrl));
      toast.success('Backup scaricato', `Database di ${CURRENT_DB_LABEL}.`);
    } catch (err) {
      toast.error('Backup non riuscito', err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const data = await parseBackupFile(file);
      setPending({ kind: 'restore', data, summary: summarizeDatabase(data), fileName: file.name });
    } catch (err) {
      toast.error('File non valido', err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleOpenCopy = async () => {
    setBusy(true);
    try {
      const data = await fetchDatabase(PRODUCTION_DB_URL);
      setPending({ kind: 'copy', data, summary: summarizeDatabase(data) });
    } catch (err) {
      toast.error('Impossibile leggere la produzione', err.message);
    } finally {
      setBusy(false);
    }
  };

  const needsTyping = pending?.kind === 'restore' && !isStagingDb;
  const canConfirm = !busy && (!needsTyping || typed.trim() === CONFIRM_WORD);

  const handleConfirm = async () => {
    if (!pending || !canConfirm) return;
    setBusy(true);
    try {
      if (backupFirst) {
        // Se il backup non riesce, l'errore interrompe l'operazione prima di scrivere.
        downloadBackup(await fetchDatabase(dbUrl));
      }
      if (pending.kind === 'copy') {
        await copyProductionToStaging();
        toast.success('Copia completata', 'Il collaudo ora riflette la produzione.');
      } else {
        await replaceDatabase(dbUrl, pending.data);
        toast.success('Database ripristinato', `Dati di ${CURRENT_DB_LABEL} sostituiti con il backup.`);
      }
      setPending(null);
      setTyped('');
      setBackupFirst(true);
    } catch (err) {
      toast.error('Operazione non riuscita', err.message);
    } finally {
      setBusy(false);
    }
  };

  const isCopy = pending?.kind === 'copy';

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Database ({CURRENT_DB_LABEL})</CardTitle>
        </CardHeader>
        <CardContent>
          <ToolRow
            label="Scarica backup"
            description={`Salva un file JSON con tutti i dati del database di ${CURRENT_DB_LABEL}.`}
          >
            <Button type="button" variant="outline" size="sm" onClick={handleDownload} disabled={busy}>
              <Download className="h-4 w-4" />
              Scarica
            </Button>
          </ToolRow>
          <ToolRow
            label="Ripristina da file"
            description={`Sostituisce tutto il database di ${CURRENT_DB_LABEL} con un backup (.json o .zip, anche esportato da Firebase).`}
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              <Upload className="h-4 w-4" />
              Carica file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.zip,application/json,application/zip"
              className="hidden"
              onChange={handleFileChosen}
            />
          </ToolRow>
          {isStagingDb && (
            <ToolRow
              label="Copia da produzione"
              description="Sostituisce il database di collaudo con una copia di quello di produzione. La produzione viene solo letta."
            >
              <Button type="button" variant="outline" size="sm" onClick={handleOpenCopy} disabled={busy}>
                <DatabaseBackup className="h-4 w-4" />
                Copia
              </Button>
            </ToolRow>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!pending} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isCopy ? 'Copiare la produzione in collaudo?' : `Ripristinare il database di ${CURRENT_DB_LABEL}?`}
            </DialogTitle>
            <DialogDescription>
              Tutti i dati attuali di <strong>{CURRENT_DB_LABEL}</strong>, risposte comprese, saranno
              sostituiti{isCopy ? ' con quelli di produzione' : ` con il contenuto di ${pending?.fileName ?? 'file'}`}.
              Non è annullabile senza un backup.
            </DialogDescription>
          </DialogHeader>
          {pending && <SummaryList items={pending.summary} />}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={backupFirst}
              onChange={(e) => setBackupFirst(e.target.checked)}
              disabled={busy}
            />
            <span>Scarica prima un backup del database di {CURRENT_DB_LABEL} (consigliato)</span>
          </label>
          {needsTyping && (
            <div className="space-y-1">
              <p className="text-sm font-semibold text-brand-danger">
                Stai modificando la PRODUZIONE. Scrivi {CONFIRM_WORD} per confermare.
              </p>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} disabled={busy} autoComplete="off" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={busy}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={!canConfirm}>
              {busy ? 'Attendere…' : isCopy ? 'Copia' : 'Ripristina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DatabaseToolsCard;
